// LocationForm.jsx — formulaire unifié (infos + conservation + risque + multi-photos)
import React, { useState, useRef } from "react";
import { supabase } from "../lib/supabaseClient";
import { Loader2, X, Thermometer, Droplets, Sun, Package, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";

const initialState = {
  name: "",
  type: "Résidence principale",
  address: "",
  notes: "",
  temperature: "",
  humidity: "",
  light: "",
  ventilation: "",
  security_measures: [],
  storage_risk: "",
  storage_notes: "",
  storage_photos: [], // tableau d'URLs
};

const securityOptions = [
  { value: "Alarme intrusion", labelKey: "locationManager.security.intrusionAlarm" },
  { value: "Vidéosurveillance", labelKey: "locationManager.security.videoSurveillance" },
  { value: "Accès restreint", labelKey: "locationManager.security.restrictedAccess" },
  { value: "Assurance spécifique", labelKey: "locationManager.security.specificInsurance" },
];

const RISK_OPTIONS = [
  { value: "faible", labelKey: "locationManager.risks.low", color: "#16a34a", bg: "#f0fdf4" },
  { value: "moyen",  labelKey: "locationManager.risks.medium", color: "#d97706", bg: "#fffbeb" },
  { value: "élevé",  labelKey: "locationManager.risks.high", color: "#dc2626", bg: "#fef2f2" },
];

const CONDITION_FIELDS = [
  { key: "temperature", labelKey: "locationManager.conditions.temperature", icon: Thermometer, placeholderKey: "locationManager.conditions.temperaturePlaceholder" },
  { key: "humidity", labelKey: "locationManager.conditions.humidityRelative", icon: Droplets, placeholderKey: "locationManager.conditions.humidityPlaceholder" },
  { key: "light", labelKey: "locationManager.conditions.lightUv", icon: Sun, placeholderKey: "locationManager.conditions.lightPlaceholder" },
  { key: "ventilation", labelKey: "locationManager.conditions.packagingVentilation", icon: Package, placeholderKey: "locationManager.conditions.ventilationPlaceholder" },
];

// Types de lieux élargis (doc RECOMMANDATIONS_..._LIEUX_CONSERVATION §2.A).
// Les valeurs (FR) sont stockées telles quelles en base ; on garde les anciennes
// pour la compatibilité et on en ajoute des plus précises (réserve, self-stockage,
// entrepôt d'art, dépôt pro, musée, transit, port franc).
const LOCATION_TYPES = [
  { value: "Résidence principale", labelKey: "locationManager.types.primaryResidence" },
  { value: "Résidence secondaire", labelKey: "locationManager.types.secondaryResidence" },
  { value: "Bureau", labelKey: "locationManager.types.office" },
  { value: "Réserve personnelle", labelKey: "locationManager.types.personalReserve" },
  { value: "Garde-meuble", labelKey: "locationManager.types.selfStorage" },
  { value: "Entrepôt d'art spécialisé", labelKey: "locationManager.types.artWarehouse" },
  { value: "Galerie / atelier / dépôt", labelKey: "locationManager.types.professionalDeposit" },
  { value: "Musée / institution", labelKey: "locationManager.types.museum" },
  { value: "Prêt", labelKey: "locationManager.types.loan" },
  { value: "Exposition", labelKey: "locationManager.types.exhibition" },
  { value: "Transport / transit", labelKey: "locationManager.types.transit" },
  { value: "Entrepôt sous douane / port franc", labelKey: "locationManager.types.freeport" },
  { value: "Autre", labelKey: "locationManager.types.other" },
];

export default function LocationForm({ onSubmit, onCancel, defaultValues = {} }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  // Normalise les photos : ancienne clé storage_photo_url (string) → tableau
  const normalizePhotos = (dv) => {
    if (Array.isArray(dv.storage_photos) && dv.storage_photos.length > 0) return dv.storage_photos;
    if (typeof dv.storage_photo_url === "string" && dv.storage_photo_url) return [dv.storage_photo_url];
    return [];
  };

  const [formData, setFormData] = useState({
    ...initialState,
    ...defaultValues,
    storage_photos: normalizePhotos(defaultValues),
  });
  const [uploading, setUploading] = useState(false);

  function handleChange(e) {
    const { name, value, checked } = e.target;
    if (name === "security_measures") {
      const updated = checked
        ? [...(formData.security_measures || []), value]
        : (formData.security_measures || []).filter((v) => v !== value);
      setFormData((prev) => ({ ...prev, security_measures: updated }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  }

  function setField(key, value) {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }

  // Upload une photo et l'ajoute au tableau
  async function handlePhoto(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const ext  = file.name.split(".").pop();
        const path = `locations/photos/photo_${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error } = await supabase.storage
          .from("artworks-images")
          .upload(path, file, { upsert: true });
        if (error) throw error;
        const { data: pub } = supabase.storage.from("artworks-images").getPublicUrl(path);
        newUrls.push(pub.publicUrl);
      }
      setFormData((prev) => ({
        ...prev,
        storage_photos: [...(prev.storage_photos || []), ...newUrls],
      }));
    } catch (err) {
      alert(t("locationManager.notifications.uploadError", { message: err.message }));
    } finally {
      setUploading(false);
    }
  }

  function removePhoto(url) {
    setFormData((prev) => ({
      ...prev,
      storage_photos: prev.storage_photos.filter((u) => u !== url),
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    // On conserve storage_photo_url (première photo) pour rétro-compatibilité
    onSubmit({
      ...formData,
      storage_photo_url: formData.storage_photos[0] || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxHeight: "75vh", overflowY: "auto", paddingRight: 4 }}>

      {/* ── 1. Infos générales ───────────────────────────────────── */}
      <div style={sSection}>
        <div style={sSectionTitle}>{t("locationManager.form.sections.general")}</div>

        <div style={sField}>
          <label style={sLabel}>{t("locationManager.form.name")} *</label>
          <input name="name" value={formData.name} onChange={handleChange} required style={sInput} />
        </div>

        <div style={sField}>
          <label style={sLabel}>{t("locationManager.fields.type")}</label>
          <select name="type" value={formData.type} onChange={handleChange} style={sInput}>
            {LOCATION_TYPES.map((option) => (
              <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
            ))}
          </select>
        </div>

        <div style={sField}>
          <label style={sLabel}>{t("locationManager.fields.address")}</label>
          <input name="address" value={formData.address} onChange={handleChange} style={sInput} />
        </div>

        <div style={sField}>
          <label style={sLabel}>{t("locationManager.fields.notes")}</label>
          <textarea name="notes" rows={3} value={formData.notes} onChange={handleChange} style={{ ...sInput, resize: "vertical" }} />
        </div>
      </div>

      {/* ── 2. Conditions de conservation ───────────────────────── */}
      <div style={sSection}>
        <div style={sSectionTitle}>{t("locationManager.form.sections.conservation")}</div>
        {CONDITION_FIELDS.map(({ key, labelKey, icon: Icon, placeholderKey }) => (
          <div key={key} style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#fafaf8", border: "1px solid #e0ddd8",
            borderRadius: 6, padding: "10px 12px", marginBottom: 8,
          }}>
            <Icon size={16} color="#c9a961" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: "#888", fontFamily: "system-ui", marginBottom: 2 }}>{t(labelKey)}</div>
              <input
                type="text"
                name={key}
                value={formData[key] || ""}
                onChange={handleChange}
                placeholder={t(placeholderKey)}
                style={{ width: "100%", border: "none", background: "transparent", fontSize: 14, fontFamily: "Georgia, serif", outline: "none", color: "#1a1a1a" }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* ── 3. Sécurité ─────────────────────────────────────────── */}
      <div style={sSection}>
        <div style={sSectionTitle}>{t("locationManager.form.sections.security")}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {securityOptions.map((option) => {
            const checked = Array.isArray(formData.security_measures) && formData.security_measures.includes(option.value);
            return (
              <label key={option.value} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 12px",
                border: `1px solid ${checked ? "#1a1a1a" : "#e0d8cc"}`,
                borderRadius: 4,
                background: checked ? "#1a1a1a" : "#fff",
                color: checked ? "#fff" : "#555",
                fontSize: 12, cursor: "pointer",
              }}>
                <input
                  type="checkbox"
                  name="security_measures"
                  value={option.value}
                  checked={checked}
                  onChange={handleChange}
                  style={{ display: "none" }}
                />
                {t(option.labelKey)}
              </label>
            );
          })}
        </div>
      </div>

      {/* ── 4. Risque global ─────────────────────────────────────── */}
      <div style={sSection}>
        <div style={sSectionTitle}>{t("locationManager.form.sections.risk")}</div>
        <div style={{ display: "flex", gap: 8 }}>
          {RISK_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setField("storage_risk", formData.storage_risk === opt.value ? "" : opt.value)}
              style={{
                flex: 1, padding: "10px 8px",
                border: `2px solid ${formData.storage_risk === opt.value ? opt.color : "#e0ddd8"}`,
                borderRadius: 6,
                background: formData.storage_risk === opt.value ? opt.bg : "#fff",
                color: formData.storage_risk === opt.value ? opt.color : "#aaa",
                fontSize: 13, fontFamily: "system-ui", fontWeight: 700,
                cursor: "pointer", transition: "all 0.15s",
              }}
            >
              {t(opt.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {/* ── 5. Observations & photos ─────────────────────────────── */}
      <div style={sSection}>
        <div style={sSectionTitle}>{t("locationManager.form.sections.observationsPhotos")}</div>

        <div style={sField}>
          <label style={sLabel}>{t("locationManager.form.generalObservations")}</label>
          <textarea
            name="storage_notes"
            value={formData.storage_notes}
            onChange={handleChange}
            placeholder={t("locationManager.form.observationsPlaceholder")}
            rows={3}
            style={{ ...sInput, resize: "vertical" }}
          />
        </div>

        {/* Galerie multi-photos */}
        <div style={sField}>
          <label style={sLabel}>{t("locationManager.photos.title")}</label>

          {/* Grille des photos existantes */}
          {formData.storage_photos.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 10 }}>
              {formData.storage_photos.map((url) => (
                <div key={url} style={{ position: "relative", borderRadius: 6, overflow: "hidden", aspectRatio: "1", background: "#f0ece5" }}>
                  <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  <button
                    type="button"
                    onClick={() => removePhoto(url)}
                    style={{
                      position: "absolute", top: 4, right: 4,
                      background: "rgba(0,0,0,0.65)", color: "#fff",
                      border: "none", borderRadius: "50%",
                      width: 24, height: 24, cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Bouton ajouter */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            style={{ display: "none" }}
            onChange={handlePhoto}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              width: "100%", padding: "12px",
              border: "2px dashed #e0ddd8", borderRadius: 6,
              background: "#fafaf8", cursor: uploading ? "not-allowed" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              fontSize: 13, color: "#888",
            }}
          >
            {uploading
              ? <><Loader2 size={15} /> {t("locationManager.photos.uploading")}</>
              : <><Plus size={15} /> {formData.storage_photos.length > 0 ? t("locationManager.photos.addMore") : t("locationManager.photos.addPhotos")}</>
            }
          </button>
        </div>
      </div>

      {/* ── Actions ──────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, paddingBottom: 4 }}>
        <button
          type="button"
          onClick={onCancel}
          style={{ padding: "9px 18px", border: "1px solid #e0d8cc", borderRadius: 4, background: "#fff", color: "#555", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          {t("locationManager.actions.cancel")}
        </button>
        <button
          type="submit"
          style={{ padding: "9px 18px", border: "none", borderRadius: 4, background: "#1a1a1a", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          {t("locationManager.actions.save")}
        </button>
      </div>
    </form>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const sSection      = { marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid #f0ece5" };
const sSectionTitle = { fontSize: 11, fontFamily: "system-ui", fontWeight: 700, letterSpacing: "0.07em", color: "#888", textTransform: "uppercase", marginBottom: 12 };
const sField        = { marginBottom: 10 };
const sLabel        = { display: "block", fontSize: 11, color: "#888", fontFamily: "system-ui", marginBottom: 4 };
const sInput        = { width: "100%", boxSizing: "border-box", border: "1px solid #e0d8cc", borderRadius: 4, padding: "8px 10px", fontSize: 13, fontFamily: "Georgia, serif", color: "#1a1a1a", outline: "none", background: "#fff" };